import { JSX, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAppStore from './stores/useAppStore';
import { useSessionInit } from './hooks/useSessionInit';
import { setupNetworkMonitoring } from './utils/networkMonitor';
import { logger, uiLogger } from './utils/logger';
import { authApi } from './services/api';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import AdminDocsPage from './pages/AdminDocsPage';
import DocsPage from './pages/DocsPage';
import ApiDocsPage from './pages/ApiDocsPage';
import ComingSoonPage from './pages/ComingSoonPage';
import ChatPage from './pages/ChatPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleBasedRoute from './components/auth/RoleBasedRoute';
import ChatWidget from './components/chat/ChatWidget';
import LoginModal from './components/forms/LoginModal';
import EmailVerificationModal from './components/forms/EmailVerificationModal';
import RegistrationModal from './components/forms/RegistrationModal';
import ForgotPasswordModal from './components/forms/ForgotPasswordModal';

function AppContent(): JSX.Element {
  const location = useLocation();
  const { isForgotPasswordModalOpen, closeForgotPasswordModal, openLoginModalClean } = useAppStore();
  
  // Check if current page should show header/footer
  const isFullPageRoute = location.pathname === '/chat';
  
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Header - Don't show on full page routes like chat */}
      {!isFullPageRoute && <Header />}

      {/* Main Content */}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          
          {/* Chat page */}
          <Route path="/chat" element={<ChatPage />} />
          
          {/* Legal pages */}
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          
          {/* Coming Soon page for regular users */}
          <Route 
            path="/coming-soon" 
            element={
              <ProtectedRoute>
                <ComingSoonPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Public documentation routes */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/api" element={<ApiDocsPage />} />
          
          <Route 
            path="/admin" 
            element={
              <RoleBasedRoute allowedRoles={['Admin']}>
                <AdminPage />
              </RoleBasedRoute>
            } 
          />
          
          <Route 
            path="/admin/docs" 
            element={
              <RoleBasedRoute allowedRoles={['Admin']}>
                <AdminDocsPage />
              </RoleBasedRoute>
            } 
          />
          
          <Route 
            path="/admin/contact" 
            element={
              <RoleBasedRoute allowedRoles={['Admin']}>
                <ComingSoonPage />
              </RoleBasedRoute>
            } 
          />
        </Routes>
      </main>

      {/* Footer - show on homepage, coming-soon, legal pages, and docs */}
      {!isFullPageRoute && (
        <Routes>
          <Route path="/" element={<Footer />} />
          <Route path="/coming-soon" element={<Footer />} />
          <Route path="/terms" element={<Footer />} />
          <Route path="/privacy" element={<Footer />} />
          <Route path="/docs" element={<Footer />} />
          <Route path="/docs/api" element={<Footer />} />
        </Routes>
      )}

      {/* Chat Widget - preserved for MVP launch, shows on all pages except chat page */}
      <ChatWidget />

      {/* Modals */}
      <LoginModal />
      <EmailVerificationModal />
      <RegistrationModal />
      <ForgotPasswordModal 
        isOpen={isForgotPasswordModalOpen}
        onClose={closeForgotPasswordModal}
        onBackToLogin={openLoginModalClean}
      />
      
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
            border: '1px solid var(--toast-border)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#FFFFFF',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#FFFFFF',
            },
          },
        }}
      />
    </div>
  );
}

function App(): JSX.Element {
  const { isDarkMode, logout } = useAppStore();
  const { isInitialized, isLoading } = useSessionInit();

  // Initialize theme and logging on app load
  useEffect(() => {
    // Check for logout parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('logout') === 'true') {
      console.log('Logout parameter detected - signing out user');
      
      // Sign out and redirect to homepage
      authApi.logout().then(() => {
        logout();
        // Remove the logout parameter and redirect to homepage
        window.location.href = '/';
      }).catch((error) => {
        console.error('Logout error:', error);
        // Even if logout fails, clear local state and redirect
        logout();
        window.location.href = '/';
      });
      
      return; // Don't continue with normal initialization
    }

    // Initialize logger system
    logger.info('🚀 Fabrik 28 Application Starting', {
      mode: import.meta.env.MODE,
      logLevel: import.meta.env.VITE_LOG_LEVEL,
      isDev: import.meta.env.DEV,
      timestamp: new Date().toISOString()
    });
    
    // Set up network monitoring for debugging
    setupNetworkMonitoring();
    
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      uiLogger.debug('Dark mode enabled');
    } else {
      document.documentElement.classList.remove('dark');
      uiLogger.debug('Light mode enabled');
    }
  }, [isDarkMode, logout]);

  // Show loading screen while initializing session
  if (isLoading || !isInitialized) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;